import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronRight, BookOpen } from 'lucide-react';
import { useQuranProgress } from '../../hooks/useQuranProgress';
import { CrescentStarIcon, GeometricPatternBg } from '../common/IslamicIcons';

const NASKH = "'Amiri', 'Noto Naskh Arabic', serif";

/**
 * QuranBanner — compact banner on the home page.
 * Opens the Quran reader in Arabic-only mode.
 * Shows "Continue Reading" if user has a saved position.
 */
const QuranBanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const { getMostRecent } = useQuranProgress();

  const recent = getMostRecent();

  const handleOpen = () => {
    // Set Arabic-only preference then open Quran
    localStorage.setItem('quran_arabic_only', 'true');
    if (recent) {
      navigate(`/quran/surah/${recent[0]}?resume=${recent[1].verse_key}`);
    } else {
      navigate('/quran?arabic=1');
    }
  };

  const bgCard = isDarkMode
    ? 'bg-gradient-to-r from-[#0f2744] via-[#1e3a5f] to-[#0f2744] border border-amber-500/15'
    : 'bg-gradient-to-r from-[#0f172a] via-[#1a365d] to-[#0f2744] border border-white/10';

  return (
    <button
      data-testid="quran-banner"
      onClick={handleOpen}
      className={`w-full rounded-2xl overflow-hidden relative text-left hover:scale-[1.01] transition-transform ${bgCard}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Geometric bg pattern */}
      <GeometricPatternBg isDarkMode={true} opacity={0.04} />
      {/* Shimmer */}
      <div className="absolute inset-0 quran-banner-shimmer pointer-events-none" />

      <div className={`relative z-10 px-4 py-3.5 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/25 flex items-center justify-center flex-shrink-0">
          <CrescentStarIcon size={20} color="#fbbf24" />
        </div>

        {/* Text */}
        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
          <p
            className="text-xl font-bold text-amber-300 leading-tight"
            style={{ fontFamily: NASKH, direction: 'rtl', letterSpacing: 0 }}
          >
            القرآن الكريم
          </p>
          {recent ? (
            <p className="text-[11px] text-white/60 mt-0.5">
              {t('quranReader.continueReading')} · {recent[1].chapter_name} {recent[1].verse_key}
            </p>
          ) : (
            <p className="text-[11px] text-white/55 mt-0.5">
              {t('quranReader.arabicOnly', 'Read in Arabic · All 114 Surahs')}
            </p>
          )}
        </div>

        {/* Arrow */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <BookOpen className="w-3.5 h-3.5 text-amber-400/70" />
          <ChevronRight className={`w-4 h-4 text-amber-400/60 ${isRTL ? 'rotate-180' : ''}`} />
        </div>
      </div>
    </button>
  );
};

export default QuranBanner;
